import { useMutation } from '@apollo/client';
import { ZonedDateTime } from '@js-joda/core';
import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import React, { useCallback, useMemo, useState } from 'react';
import { compose, withProps } from 'recompose';
import { setPatientEventTimestamp } from '../../../../graph/procedureSteps';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import { withLabel } from '@/se/components/Label';
import { RouterlessModal } from '@/se/components/Modal';
import { CancelableForm, DateInput, formatTime } from '../../../inputs/timeEdit/TimeEditForm';
import DateDropdownInput from '@/se/components/inputs/DateInput';

export const convertToDateTime = (timestamp, time, date) => {
  const dateTime = new Date(timestamp);
  if (date) {
    const [year, month, day] = date.split('-').map(e => parseInt(e, 10));
    if (isFinite(year) && isFinite(month) && isFinite(day)) {
      dateTime.setFullYear(year, month - 1, day);
    }
  }
  const [hours, minutes] = time.split(':').map(e => parseInt(e, 10));
  dateTime.setHours(hours);
  dateTime.setMinutes(minutes);
  return dateTime;
};

const getEventType = event => {
  switch (event?.type) {
    case 'Created':
    case 'Discharged':
    case 'BlockNerve':
    case 'CleaningCompleted':
    case 'OrRoomReady':
      return { __type: `${event?.type}$` };
    case 'TransferredToChair':
    case 'TransferredToPT':
    case 'PTComplete':
      return { __type: `${event?.type}`, manualEntryBy: null, manualTimestamp: null };
    case 'Entered':
      return { __type: 'Entered', roomId: event?.room?.id, manualEntryBy: null, manualTimestamp: null };
    case 'Exited':
      return { __type: 'Exited', roomId: event?.room?.id, manualEntryBy: null };
    case 'ProcedureStatusSet':
      return {
        __type: 'ProcedureStatusSet',
        status: { [event?.status]: {} },
        manualEntryBy: null,
        manualTimestamp: null,
      };
    default:
      return null;
  }
};

export const FormInput = (eventText, withDate = false, options: any = {}) =>
  withProps({
    schema: {
      ...(withDate && {
        date: compose(
          withLabel('Date'),
          withProps({
            defaultDate: options.defaultDate,
          })
        )(DateDropdownInput),
      }),
      time: compose(
        withLabel(eventText),
        withProps({
          autoFocus: true,
          required: true,
        })
      )(DateInput),
    },
  })(ObjectInput);

const PatientEventEdit = ({ patientId, event, eventText, editable = false }) => {
  const [edit, setEdit] = useState(false);
  const [save] = useMutation(setPatientEventTimestamp);
  const includeDate = event?.type === 'Discharged';

  const toggleEdit = useCallback(() => {
    setEdit(prev => !prev);
  }, []);

  const saveTimestamp = useCallback(
    async (time, date) => {
      if (!time) {
        toggleEdit();
        return;
      }
      const eventType = getEventType(event);
      const newDateTime = convertToDateTime(event?.timestamp, time, date);
      newDateTime.toISOString() !== new Date(event?.timestamp).toISOString() &&
        patientId &&
        eventType &&
        (await save({
          variables: {
            patientId,
            eventType: JSON.stringify(eventType),
            timestamp: newDateTime,
          },
        }));
      toggleEdit();
    },
    [event, patientId, save, toggleEdit]
  );

  const onSubmit = useCallback(
    async val => {
      await saveTimestamp(val?.time, val?.date);
    },
    [saveTimestamp]
  );

  const eventZdt = useMemo(
    () => (event?.timestamp ? ZonedDateTime.parse(event.timestamp) : undefined),
    [event?.timestamp]
  );
  const initialTime = useMemo(
    () => (eventZdt ? formatTime(eventZdt.toLocalDateTime().toString()) : undefined),
    [eventZdt]
  );
  const initialDate = useMemo(() => (eventZdt ? eventZdt.toLocalDate().toString() : undefined), [eventZdt]);
  const defaultDate = useMemo(() => (event?.timestamp ? new Date(event.timestamp) : undefined), [event?.timestamp]);
  const inputComponent = useMemo(
    () => FormInput(eventText, includeDate, { defaultDate }),
    [eventText, includeDate, defaultDate]
  );

  return (
    <>
      {editable && (
        <IconButton onClick={toggleEdit} size="small" color="primary">
          <EditIcon />
        </IconButton>
      )}
      {editable && edit && (
        <RouterlessModal title="Update Time" onClose={toggleEdit}>
          <CancelableForm
            autoFocus
            label="Update"
            onClose={toggleEdit}
            initialValue={{
              time: initialTime,
              ...(includeDate ? { date: initialDate } : {}),
            }}
            input={inputComponent}
            onSubmit={onSubmit}
            busy={false}
          />
        </RouterlessModal>
      )}
    </>
  );
};

export default PatientEventEdit;
